import React, { Component } from "react";
import { pickConverter, Converter } from "./workspace/converter";
import Utils from "context/utils";

export default class Synchronizer {
  constructor(app, revision, shell, sessionManager) {
    this.app = app;
    this.revision = revision;
    this.shell = shell;
    this.sessionManager = sessionManager;
    this.mounted = false;
  }

  /**
   * Persistence
   */

  savePlayground() {
    // Display progress
    this.shell.shellInterface.updateProgress(true, "Saving playground definition...", false, null);

    // Convert ReactFlow representation into BlobHub workflow definition object
    let state = this.shell.shellInterface.getState();
    const converter = pickConverter(state, this.shell.shellInterface);
    const definitionObject = converter.reactFlowToBlobhubDefinition(state.nodes, state.edges);

    // Preserve the layout block from the persisted stub. The backend treats
    // most of the layout block as immutable: omitting it on save would make
    // grid playgrounds fail the immutability check (absent canonicalizes to
    // free_form). Re-emit whatever the loaded definition carries, but
    // overlay the current zoom — that field is the one mutable property of
    // the layout (the backend's canonicalize_layout ignores it).
    const persistedLayout = state.definitionObjectBackup?.layout;
    if (persistedLayout) {
      definitionObject.layout = { ...persistedLayout, zoom: state.zoom ?? 1 };
    } else if (state.zoom != null && state.zoom !== 1) {
      // Legacy free-form playground with no layout block — only emit one
      // when the user has explicitly changed zoom from the default.
      definitionObject.layout = { type: "free_form", zoom: state.zoom };
    }

    // Upload new definition
    const body = {
      engine: "workflow_blobhub",
      command: "upload_definition",
      definition_id: state.definitionId,
      definition: definitionObject
    };
    this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then( response => {
        // Remove progress
        this.shell.shellInterface.updateProgress(false, null, false, null);

        // Notify the view
        this.shell.shellInterface.updateState({
          definitionObjectBackup: definitionObject,
          modified: false
        });
      }, error => {
        // Render error state
        this.shell.shellInterface.updateProgress(
          false, null, true, "Failed to save workflow definition: " + error.message);
      });
  }

  revertPlayground() {
    // Recreate workspace state based on cached definition
    let state = this.shell.shellInterface.getState();
    const converter = pickConverter(state, this.shell.shellInterface);
    const [initialNodes, initialEdges] = converter.blobhubDefinitionToReactFlow(
      state.definitionObjectBackup,
      state.manifest
    );

    // Restore the persisted zoom too — the layout block's zoom field is
    // saved alongside the rest of the definition, so reverting must roll
    // it back to whatever was last loaded.
    const persistedZoom = state.definitionObjectBackup?.layout?.zoom;
    const zoom = (typeof persistedZoom === "number" && persistedZoom > 0) ? persistedZoom : 1;

    // Notify the view
    this.shell.shellInterface.updateState({
      nodes: initialNodes,
      edges: initialEdges,
      zoom: zoom,
      modified: false
    });
  }

  /**
   * Initial Sequence
   *
   * The following flow is performed:
   * - loadPrerequisites
   * - loadWorkflowDefinitions
   * - loadPlaygroundManifest
   * - loadPlaygroundDefinitions
   *   - createDefaultPlayground (if non exists)
   * - loadPlaygroundDefinition
   * - loadSessions
   */

  loadPrerequisites(reference) {
    this.loadWorkflowDefinitions(reference);
  }

  loadWorkflowDefinitions(reference) {
    // Indicate progress
    this.shell.shellInterface.updateProgress(true, "Loading workflow definitions...", false, null);

    // Load playground definitions
    const body = {
      engine: "workflow_blobhub",
      command: "list_definitions",
      category: "workflow"
    };
    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then( response => {
        // Extract definitions from the response body
        const definitions = response.data.definitions;

        // Notify the view that definitions have been fetched
        this.shell.shellInterface.updateState({
          workflowDefinitions: definitions
        }).then(() => {
          this.loadPlaygroundManifest(reference);
        });
      }, error => {
        // Indicate fatal failure
        this.shell.shellInterface.updateState({
          fatal: true, fatalMessage: "Failed to load workflow definitions: " + error.message
        });
      });
  }

  loadPlaygroundManifest(reference) {
    // Indicate progress
    this.shell.shellInterface.updateProgress(true, "Loading playground manifest...", false, null);

    // Load manifest
    this.app.api.workflowManifestGet(this.revision.id, "playground")
      .then( response => {
        // Extract manifest from the response body
        let manifest = response.data.manifest;

        // Post process manifest data structure
        Converter.parseManifest(manifest);

        // Notify the view that the manifest has been fetched
        this.shell.shellInterface.updateState({
          manifest: manifest
        }).then(() => {
          this.loadPlaygroundDefinitions(reference);
        });
      }, error => {
        // Indicate fatal failure
        this.shell.shellInterface.updateState({
          fatal: true, fatalMessage: "Failed to load playground manifest: " + error.message
        });
      });
  }

  loadPlaygroundDefinitions(reference) {
    // Indicate progress
    this.shell.shellInterface.updateProgress(true, "Loading playground definitions...", false, null);

    // Load playground definitions
    const body = {
      engine: "workflow_blobhub",
      command: "list_definitions",
      category: "playground"
    };
    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then( response => {
        // Extract definitions from the response body
        const definitions = response.data.definitions;

        // Notify the view that definitions have been fetched
        this.shell.shellInterface.updateState({
          definitions: definitions
        }).then(() => {
          // See if we have a reference to select
          let definition = null;
          if ( reference ) {
             for ( const item of definitions ) {
              if ( item["alias"] == reference || item["id"] == reference ) {
                definition = item;
                break;
              }
            }
          }

          // If no reference, check if there is a default selection already
          // (not implemented here as we rely on shell state usually, but let's be safe)
          
          // If still no definition, default to first or create default
          if ( definitions.length > 0 ) {
            // Auto-select default definition if none selected
            if ( !definition ) {
                definition = definitions[0];
            }

            // Apply current selection
            this.shell.shellInterface.updateState({
              definition: definition,
              definitionId: definition["id"]
            }).then(() => {
              this.loadPlaygroundDefinition();
            });
          } else {
            this.createDefaultPlayground();
          }
        });
      }, error => {
        // Indicate fatal failure
        this.shell.shellInterface.updateState({
          fatal: true, fatalMessage: "Failed to load playground definitions: " + error.message
        });
      });
  }

  createDefaultPlayground() {
    // Indicate progress
    this.shell.shellInterface.updateProgress(true, "Creating default playground definition...", false, null);

    // Create "default" definition
    const alias = "default";
    const body = {
      engine: "workflow_blobhub",
      command: "create_definition",
      category: "playground",
      alias: alias
    };
    this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then( response => {
        // Retrieve definition
        const definition = response.data["definition"];
        const definitionId = definition["id"];
        const definitions = [ definition ];

        // Notify the view
        this.shell.shellInterface.updateState({
          definitions: definitions,
          definitionId: definitionId,
          definition: definition
        }).then(() => {
          this.loadPlaygroundDefinition();
        });
      }, error => {
        // Indicate fatal failure
        this.shell.shellInterface.updateState({
          fatal: true, fatalMessage: "Failed to create definition: " + error.message
        });
      });
  }

  loadPlaygroundDefinition() {
    // Indicate progress
    this.shell.shellInterface.updateProgress(true, "Loading playground definition...", false, null);

    // Load definition object
    let state = this.shell.shellInterface.getState();
    const body = {
      engine: "workflow_blobhub",
      command: "download_definition",
      definition_id: state.definitionId
    };
    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then( response => {
        // Extract definition from the response
        const definitionObject = response.data.definition_object;

        // Convert definition. Pick the converter from the just-fetched JSON
        // (state.definitionObjectBackup hasn't been updated yet).
        state = this.shell.shellInterface.getState();
        const converter = pickConverter(definitionObject, this.shell.shellInterface);
        const [initialNodes, initialEdges] = converter.blobhubDefinitionToReactFlow(
          definitionObject,
          state.manifest
        );

        // Restore persisted zoom from the layout block (optional; defaults
        // to 1 when absent, e.g. legacy or just-created playgrounds).
        const persistedZoom = definitionObject?.layout?.zoom;
        const initialZoom = (typeof persistedZoom === "number" && persistedZoom > 0) ? persistedZoom : 1;

        // Notify the view
        this.shell.shellInterface.updateState({
          definitionObjectBackup: Utils.deepCopy(definitionObject),
          nodes: initialNodes,
          edges: initialEdges,
          zoom: initialZoom,
        }).then(() => {
        }).then(() => {
          this.loadSessions();
        });
      }, error => {
        // Indicate fatal failure
        this.shell.shellInterface.updateState({
          fatal: true, fatalMessage: "Failed to load definition: " + error.message
        });
      });
  }

  loadSessions(more) {
    this.sessionManager.loadSessions(more)
      .then(sessions => {
        if (!more) {
            this.shell.shellInterface.selectSession();
        }
        // Trigger update in PlaygroundShell to re-render Selector if needed
        // Although Selector should ideally use SessionManager directly or observables
        this.shell.shellInterface.updateState({}); 
      })
      .catch(error => {
        this.shell.shellInterface.updateProgress(
        false, null, true, "Failed to load the list of sessions: " + error.message);
      });
  }
}
